import React, { useEffect, useRef } from 'react';

interface UseGigVisibilityObserverProps {
    targetRef: React.RefObject<HTMLElement>;
    callback: () => void;
}

/**
 * Observer hook to detect when a specific component instance is hidden through CSS styles
 * Checks every 5 seconds until the target element or its children/grandchildren are hidden, then stops.
 * Only observes the specific component instance, not siblings or other instances.
 * Detects display: none applied via:
 * - Inline styles (style attribute)
 * - CSS classes
 * - External stylesheets
 * - Computed styles
 * - display: none !important
 */
const useGigVisibilityObserver = ({ targetRef, callback }: UseGigVisibilityObserverProps) => {
    const hasDetectedRef = useRef(false);

    useEffect(() => {
        if (!targetRef.current || hasDetectedRef.current) {
            return;
        }

        const checkIfHidden = (element: HTMLElement): boolean => {
            const computedStyle = window.getComputedStyle(element);
            const display = computedStyle.display;
            return display === 'none';
        };

        // Get children and grandchildren (2 levels deep) of the target element
        const getChildrenAndGrandchildren = (element: HTMLElement): HTMLElement[] => {
            const children = Array.from(element.children) as HTMLElement[];
            const grandchildren: HTMLElement[] = [];

            // Get all grandchildren
            for (const child of children) {
                const childGrandchildren = Array.from(child.children) as HTMLElement[];
                grandchildren.push(...childGrandchildren);
            }

            return [...children, ...grandchildren];
        };

        const checkTargetElementAndDescendants = () => {
            const targetElement = targetRef.current;
            if (!targetElement || hasDetectedRef.current) {
                return;
            }

            // Check the target element itself
            if (checkIfHidden(targetElement)) {
                hasDetectedRef.current = true;
                callback();
                return;
            }

            // Check children and grandchildren of the target element only
            const childrenAndGrandchildren = getChildrenAndGrandchildren(targetElement);
            for (const descendant of childrenAndGrandchildren) {
                if (checkIfHidden(descendant)) {
                    hasDetectedRef.current = true;
                    callback();
                    return;
                }
            }
        };

        // Check every 5 seconds until detection
        const intervalId = setInterval(() => {
            checkTargetElementAndDescendants();

            // Stop interval if detection occurred
            if (hasDetectedRef.current) {
                clearInterval(intervalId);
            }
        }, 5000);

        // Cleanup function
        return () => {
            clearInterval(intervalId);
        };
    }, [targetRef, callback]);
};

export default useGigVisibilityObserver;
